import React, { useEffect, useMemo } from 'react';
import { useHandleFreshServiceTicketUpsert } from '../../hooks/query/freshService/freshServiceQueries';
import { IOptionType, ProjectDialog } from '@zz2/zz2-ui';
import FreshServiceTicketModelHelper from '../../@types/model/freshService/freshServiceTicketModelHelper';
import { useLocation } from 'react-router';
import EnvHelper from '../../service/helper/envHelper';
import { VERSION } from '../../version';
import { useAppSelector } from '../../@types/redux';
import FreshServiceTicketForm from './FreshServiceTicketDialogForm';
import { IFreshServiceTicketFormValues } from '../../@types/model/freshService/freshServiceTicketFormValues';
import { useGetUsers } from '../../hooks/query/rights/userQueries';

interface IFreshServiceTicketEditDialogProps {
    isOpen : boolean;
    onDismiss : () => void;
    title ?: string;
    description ?: string;
}

const FreshServiceTicketCreationDialog = (props : IFreshServiceTicketEditDialogProps) : React.ReactElement => {
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    const { isLoading: isUpsertingFreshServiceTicket, mutate: upsertFreshServiceTicket, isSuccess: upsertSuccess } = useHandleFreshServiceTicketUpsert();
    const { isLoading: isLoadingUsers, data: users } = useGetUsers();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (upsertSuccess) {
            props.onDismiss();
        }
    }, [upsertSuccess]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ===============================================================================================================*/

    const onSubmit = async (values : IFreshServiceTicketFormValues) : Promise<void> => {
        const upsert = FreshServiceTicketModelHelper.createUpsert(values);
        
        upsertFreshServiceTicket(upsert);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo(() => {
        return isUpsertingFreshServiceTicket
        || isLoadingUsers;
    }, [
        isUpsertingFreshServiceTicket,
        isLoadingUsers
    ]);

    const userOptions = useMemo<Array<IOptionType>>(() => {
        return users?.map(x => {
            return {
                label: x.name,
                value: x.id
            };

        }) ?? [];

    }, [users]);

    const initialFormValues = useMemo<IFreshServiceTicketFormValues>(() => {
        const systemName = EnvHelper.getEnvName();

        return FreshServiceTicketModelHelper.createFormValues(
            systemName,
            VERSION.version,
            location.pathname,
            currentUser?.name ?? '',
            currentUser?.email ?? '',
            props.description ?? ''
        );
    }, [location.pathname, currentUser, props.description, props.isOpen]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <ProjectDialog
            title={'ZZ2 FRESH SERVICE SUPPORT TICKET'}
            isLoadingCircular={isLoading}
            isOpen={props.isOpen}
            fullWidth
            maxWidth={'sm'}
            onClose={props.onDismiss}>
            <FreshServiceTicketForm 
                initialValues={initialFormValues} 
                onSubmit={onSubmit} 
                onCancel={props.onDismiss} 
                userOptions={userOptions}
                isLoading={isLoading} />
        </ProjectDialog>
    );
};

export default FreshServiceTicketCreationDialog;